import { useEffect, useState, useCallback } from '@wordpress/element';
import { useSelect, useDispatch } from '@wordpress/data';

export const Block = ({ checkoutExtensionData, extensions }) => {

	/**
	 * setExtensionData will update the wc/store/checkout data store with the values supplied. It
	 * can be used to pass data from the client to the server when submitting the checkout form.
	 */
	const { setExtensionData } = checkoutExtensionData;

	// Array of input keys
	const inputKeys = [
		'shipping_ffl_ttg_option', 
		'shipping_ffl_ttg_chosen_ffl', 
		'shipping_ffl_ttg_chosen_ffl_expiration',
		'shipping_ffl_ttg_license_file',
		'shipping_ffl_ttg_license_number',

		'shipping_ffl_ttg_cart_has_sot',
		'shipping_ffl_ttg_option_sot',
		'shipping_ffl_ttg_sot_file',

		'overwrite_ffl_ttg_address_1',
		'overwrite_ffl_ttg_address_2',
		'overwrite_ffl_ttg_city',
		'overwrite_ffl_ttg_state',
		'overwrite_ffl_ttg_postcode',
		'overwrite_ffl_ttg_country',
	];

	// State for all inputs
	const [inputValues, setInputValues] = useState(
		inputKeys.reduce((acc, key) => ({ ...acc, [key]: '' }), {})
	);

	const { setValidationErrors, clearValidationError } = useDispatch(
		'wc/store/validation'
	);

	const [newShippingAddress, setNewShippingAddress] = useState({
		address_1: '',
		address_2: '',
		city: '',
		state: '',
		postcode: '',
		country: 'US',
	});

	const { setShippingAddress } = useDispatch('wc/store/cart');
    const { hasFinishedResolving } = useSelect((select) => ({
        hasFinishedResolving: select('wc/store/cart').hasFinishedResolution('getShippingAddress'),
    }));

	// Update shipping address when inputs are updated and resolved
 	const handleUpdateShippingAddress = () => {
        setShippingAddress(newShippingAddress);
    };

	/**
	 * List of possible validation errors
	 */
	const selectedOptionValidation = useSelect( ( select ) => {
		const store = select( 'wc/store/validation' );
		return store.getValidationError( "ttg-ffl-selected-option-missing" );
	});

	const emptyChosenFFlValidation = useSelect( ( select ) => {
		const store = select( 'wc/store/validation' );
		return store.getValidationError( "ttg-ffl-empty-chosen-ffl" );
	});

	const emptyUploadedFileFFlValidation = useSelect( ( select ) => {
		const store = select( 'wc/store/validation' );
		return store.getValidationError( "ttg-ffl-empty-uploaded-ffl" );
	});

	const emptyUploadedSOTFileValidation = useSelect( ( select ) => {
		const store = select( 'wc/store/validation' );
		return store.getValidationError( "ttg-ffl-empty-sot-file" );
	});

	const emptySOTOptionValidation = useSelect( ( select ) => {
		const store = select( 'wc/store/validation' );
		return store.getValidationError( "ttg-ffl-empty-sot-option" );
	});

	const [updatedKey, setUpdatedKey] = useState(null);
    const [updatedValue, setUpdatedValue] = useState(null);

	// Handle input change
	const handleInputChange = (key) => (event) => {
		const newValue = event.target.value;

		setUpdatedKey(key); // Track the specific key being updated
        setUpdatedValue(newValue); // Track the new value for that key
		setInputValues((prevValues) => ({
			...prevValues,
			[key]: newValue,
		}));

		setExtensionData('ffl-checkout-by-ttg-for-woocommerce', key, newValue);
	};

	useEffect(() => {

        if (updatedKey?.startsWith("overwrite_ffl_ttg_")) {
            setNewShippingAddress((prevAddress) => {
                const updatedAddress = {
                    ...prevAddress,
                    [updatedKey.replace("overwrite_ffl_ttg_", "")]: updatedValue,
                };
				setShippingAddress(updatedAddress);
                return updatedAddress;
            });
        }

    }, [updatedKey, updatedValue]);

	/* Log the changing input's value */
	useEffect(() => {

		const divExists = document.getElementById("ffl-checkout-by-ttg-options-wrapper");

		if (!divExists) {
			return;
		}

		if (inputValues.shipping_ffl_ttg_option.length == 0) {

			setValidationErrors({
				["ttg-ffl-selected-option-missing"] : {
					message: "Please select an option to handle your FFL transfer",
					hidden: true,
				},
			});

		} else {
			clearValidationError( "ttg-ffl-selected-option-missing" );
		}

		if (inputValues.shipping_ffl_ttg_option == "pick_ffl" && inputValues.shipping_ffl_ttg_chosen_ffl.length == 0) {

			setValidationErrors({
				["ttg-ffl-empty-chosen-ffl"] : {
					message: "Please select an FFL from the list",
					hidden: true,
				},
			});

		} else {
			clearValidationError( "ttg-ffl-empty-chosen-ffl" );
		}

		if (inputValues.shipping_ffl_ttg_option == "upload_ffl" && inputValues.shipping_ffl_ttg_license_file.length == 0) {

			setValidationErrors({
				["ttg-ffl-empty-uploaded-ffl"] : {
					message: "Please upload or select an FFL license",
					hidden: true,
				},
			});

		} else {
			clearValidationError( "ttg-ffl-empty-uploaded-ffl" );
		}

		if (inputValues.shipping_ffl_ttg_cart_has_sot && inputValues.shipping_ffl_ttg_option_sot.length == 0 && inputValues.shipping_ffl_ttg_option != "skip_step") {

			setValidationErrors({
				["ttg-ffl-empty-sot-option"] : {
					message: "Please select an option to handle the SOT",
					hidden: true,
				},
			});

		} else {
			clearValidationError( "ttg-ffl-empty-sot-option" );
		}

		if (inputValues.shipping_ffl_ttg_cart_has_sot && inputValues.shipping_ffl_ttg_option_sot == "upload_sot" && inputValues.shipping_ffl_ttg_sot_file.length == 0 && inputValues.shipping_ffl_ttg_option != "skip_step") {

			setValidationErrors({
				["ttg-ffl-empty-sot-file"] : {
					message: "Please upload a valid SOT",
					hidden: true,
				},
			});

		} else {
			clearValidationError( "ttg-ffl-empty-sot-file" );
		}


	  }, [inputValues]);

	return (
		<div style={{ display: 'none' }} aria-hidden="false">
			{inputKeys.map((key) => (
				<div>
				<label>{key}</label><br></br>
				<input
					id={"ffl-checkout-by-ttg-for-woocommerce-input-" + key}
					key={key}
					type="text"
					value={inputValues[key]}
					onChange={handleInputChange(key)}
				/></div>
			))}

			<div style={{ display: 'none' }} id="ffl-checkout-by-ttg-for-woocommerce-errors-block">
				{ selectedOptionValidation?.hidden ? null : (
					<span> { selectedOptionValidation?.message } </span>
				)}

				{ emptyChosenFFlValidation?.hidden ? null : (
					<span> { emptyChosenFFlValidation?.message } </span>
				)}

				{ emptyUploadedFileFFlValidation?.hidden ? null : (
					<span> { emptyUploadedFileFFlValidation?.message } </span>
				)}

				{ emptyUploadedSOTFileValidation?.hidden ? null : (
					<span> { emptyUploadedSOTFileValidation?.message } </span>
				)}

				{ emptySOTOptionValidation?.hidden ? null : (
					<span> { emptySOTOptionValidation?.message } </span>
				)}

			</div>
			
		</div>
	);
};
